// @flow
import React from 'react'
import { httpDelete } from 'lib/http'
import { Table, Row, Cell } from 'components/Table'
import formatters from 'lib/formatters'
import { S3FilePreview } from 'components/S3FilePreview'
import AttachmentForm from './AttachmentForm'
import { unsetModal, setModal } from 'lib/modal/actions'
import { setBanner, setLoad, setUnload } from 'lib/notification/actions'
import { Link } from 'react-router-dom'
import type { File, LineItem, Note } from 'lib/types'
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs'
import { Notes } from 'components/Notes'
import Header from './Header/Header.js'
import styles from './styles'
import { DeleteLineItem } from '../DeleteLineItem/index.js'
import EditLineItemForm from '../EditLineItem/index.js'
import AssetServicesAccordion from '../AssetServicesAccordion/index.js'
import AccountInfoSection from '../AccountInfoSection'
import VehicleInfoSection from '../VehicleInfoSection'

type Props = {
  attachments: [File],
  attachFile: Function,
  balance: number,
  beneficiaryName: string,
  lineItems: [LineItem],
  notes: [Note],
  number: string,
  open_date: string,
  created_at: string,
  updated_at: string,
  close_date: string,
  subaccount_type: string,
  subnumber: string,
  dispatch: Function,
  openNewLineItemModal: Function,
  onSuccess: Function,
  statement_info: string,
  asset?: Object,
  vehicle?: Object,
}

const openAttachmentModal = (number, subnumber, dispatch, onSuccess) => {
  const modalForm = <AttachmentForm number={number} subnumber={subnumber} onSuccess={onSuccess} />
  dispatch(setModal(modalForm))
}

const deleteFile = (dispatch, onSuccess, number, subnumber, fileId) => {
  return (
    <section>
      <div className="section">
        <h4>Are you sure you want to delete this file?</h4>
      </div>
      <div className={styles.buttons}>
        <button className={styles.removeBtn} onClick={() => handleDelete(dispatch, onSuccess, number, subnumber, fileId)}>
          Delete File
        </button>
        <span className={styles.spacer} />
        <button className={styles.cancelBtn} onClick={() => dispatch(unsetModal())}>
          Cancel
        </button>
      </div>
    </section>
  )
}

const handleDelete = (dispatch, onSuccess, number, subnumber, fileId) => {
  dispatch(setLoad())
  const url = `/admin/accounts/${number}/subaccounts/${subnumber}/attachments/${fileId}`
  return httpDelete(url)
    .then(() => {
      dispatch(onSuccess)
      dispatch(setBanner('Document successfully deleted'))
    })
    .catch(() => {
      dispatch(setBanner('File deletion failed', 'danger'))
    })
    .then(() => {
      dispatch(unsetModal())
      dispatch(setUnload())
    })
}

const renderLineItemRow = (item: LineItem, idx: number, onEdit: (idx: number) => void, onDelete: (idx: number) => void) => (
  <Row key={`line-item-${idx}`} onEdit={() => onEdit(idx)} onDelete={() => onDelete(idx)} isEditable={item['editable?']}>
    <Cell value={formatters.date(item.effective_date)} />
    <Cell value={formatters.money(item.amount)} mono={true} />
    <Cell value={formatters.money(item.balance)} mono={true} />
    <Cell value={item.memo} />
  </Row>
)

const showAttachments = ({ attachments, number, subnumber, dispatch, onSuccess }: Props) => {
  if (!Array.isArray(attachments)) return null
  let docs
  if (attachments.length) {
    docs = attachments.map((attachment, idx) => {
      const { file_id } = attachment
      const deleteHandler = () => {
        dispatch(setModal(deleteFile(dispatch, onSuccess, number, subnumber, file_id)))
      }
      return (
        <div className={styles.otherDocsRow} key={idx}>
          {showLink(attachment)}
          <div className={styles.remove}>
            <a title="Delete File" onClick={deleteHandler} />
          </div>
        </div>
      )
    })
  } else {
    docs = <div className={styles.noDocResults}>No documents</div>
  }
  return (
    <section>
      <div className={styles.docsWrapper}>{docs}</div>
    </section>
  )
}

const showLink = (file) => <S3FilePreview file_key={file.key} label={file.name} />

const renderBackLink = (number) => {
  const accountUrl = `/accounts/${number}`
  return <Link to={accountUrl}>Back to {number}</Link>
}

export const Template = (props: Props) => {
  const {
    beneficiaryName,
    lineItems,
    notes,
    balance,
    number,
    account,
    subaccount_type,
    subnumber,
    dispatch,
    openNewLineItemModal,
    onSuccess,
    showActions = false,
    asset,
    vehicle,
    statement_info,
    status,
    created_at,
    updated_at,
    close_date,
    open_date,
  } = props

  const tableTitle = 'Transactions'

  const addTransactionButton = (
    <Link className={styles.transactionButton} onClick={openNewLineItemModal}>
      Add Transaction
    </Link>
  )

  const generateStatementButton = (
    <Link className={styles.statementButton} to={`/accounts/${number}/subaccounts/${subnumber}/statement`}>
      Generate Statement
    </Link>
  )
  const editSubaccountButton = (
    <Link className={styles.editSubaccountButton} to={`/accounts/${number}/subaccounts/${subnumber}/edit`}>
      Edit Subaccount
    </Link>
  )

  const handleEditTransaction = (idx) => {
    dispatch(
      setModal(
        <EditLineItemForm
          number={number}
          subnumber={subnumber}
          lineItem={lineItems[idx]}
          onSuccess={onSuccess}
          dispatch={dispatch}
          initialValues={{
            date: lineItems[idx].effective_date,
            amount: lineItems[idx].amount,
            memo: lineItems[idx].memo,
          }}
        />,
      ),
    )
  }

  const handleDeleteTransaction = (idx) => {
    dispatch(setModal(<DeleteLineItem number={number} subnumber={subnumber} id={lineItems[idx].id} dispatch={dispatch} onSuccess={onSuccess} />))
  }

  const renderedLineItems = lineItems
    ? lineItems.map((item, idx) => renderLineItemRow(item, idx, handleEditTransaction, handleDeleteTransaction))
    : []

  const headers = [
    { value: 'Date' },
    { value: 'Change in Value', type: 'mono' },
    { value: 'Balance', type: 'mono' },
    { value: 'Memo' },
    ...(showActions ? [{ value: 'Edit' }, { value: 'Delete' }] : []),
  ]

  return (
    <div>
      <Breadcrumbs crumbs={[renderBackLink(number)]} />
      <section className={styles.contentContainer}>
        <section className={styles.twoThird}>
          <Header balance={balance} beneficiary_name={beneficiaryName} number={number} subaccount_type={subaccount_type} subnumber={subnumber} />
          <div className={styles.table}>
            <Table title={tableTitle} headers={headers} tableSize="large" tableType="read" showActions={true}>
              {renderedLineItems}
            </Table>
          </div>
        </section>
        <div className={styles.oneThird}>
          <div className={styles.actions}>
            {addTransactionButton}
            {generateStatementButton}
            {editSubaccountButton}
          </div>
          <div className={styles.cardMarginBot}>
            <AccountInfoSection
              account={account}
              status={status}
              statement_info={statement_info}
              created_at={created_at}
              updated_at={updated_at}
              close_date={close_date}
              subaccount_type={subaccount_type}
              open_date={open_date}
            />
          </div>
          {vehicle && <VehicleInfoSection vehicle={vehicle} />}
          {asset && (
            <div className={styles.cardMarginBot}>
              <>
                <div className={styles.assetHeader}>
                  <div className={styles.assetLeft}>
                    <div className={styles.title}>
                      <h5>{formatters.legibleSubaccountType(subaccount_type)}</h5>
                    </div>
                    <div className={styles.singleLine}>
                      <div className={styles.purchaseDate}>Purchase date: {formatters.date(asset.purchase_date) || 'Not available'}</div>
                    </div>
                  </div>
                  <div className={styles.assetRight}>
                    <div className={styles.label}>
                      {asset.street_1}
                      {asset.street_2 ? ` #${asset.street_2}` : ''}
                    </div>
                    <div className={styles.label}>
                      {asset.city}, {asset.state} {asset.zip}
                    </div>
                  </div>
                </div>
                <AssetServicesAccordion asset={asset} />
              </>
            </div>
          )}
          <Notes accountNumber={number} dispatch={dispatch} notes={notes} subnumber={subnumber} type="subaccount" />
          <section data-active={true} className={styles.collapseRef}>
            <div className={styles.titleSection}>
              <div className={styles.title}>
                <h5>Attachments</h5>
              </div>
              <span
                className={styles.leaveNoteIcon}
                onClick={() => openAttachmentModal(number, subnumber, dispatch, onSuccess)}
                title="Attach File"
              />
            </div>
            {showAttachments(props)}
          </section>
        </div>
      </section>
    </div>
  )
}
